import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, FileText, Users, Target, Workflow, CheckCircle, Download, BarChart3, ClipboardList, Building2, Lightbulb, TrendingUp, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import Header from '../Header';
import Footer from '../Footer';
import BIMMaturityAssessment from './BIMMaturityAssessment';
import SEO from '../SEO';

// Componentes modulares
import { MethodologyHero, ImplantacaoVsImplementacao, EtapaLevantamento } from './sections';

const MethodologyPage = () => {
  const navigate = useNavigate();
  const [showMaturityAssessment, setShowMaturityAssessment] = useState(false);
  const maturityRef = useRef(null);

  const scrollToMaturity = () => {
    setShowMaturityAssessment(true);
    setTimeout(() => {
      maturityRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Informações baseadas no PMBOK 6ª Edição
  const pmbokAreas = [
    {
      area: 'Gestão da Integração',
      items: ['Termo de abertura do projeto BIM', 'Plano de gerenciamento do projeto', 'Controle integrado de mudanças'],
      icon: Workflow
    },
    {
      area: 'Gestão do Escopo',
      items: ['Requisitos BIM dos stakeholders', 'Definição de LOD/LOI', 'Estrutura analítica BIM (EAP)'],
      icon: Target
    },
    {
      area: 'Gestão do Cronograma',
      items: ['Fases de implementação', 'Marcos de maturidade', 'Dependências entre processos'],
      icon: ClipboardList
    },
    {
      area: 'Gestão dos Custos',
      items: ['Investimento em tecnologia', 'Custos de capacitação', 'ROI esperado do BIM'],
      icon: TrendingUp
    },
    {
      area: 'Gestão da Qualidade',
      items: ['Padrões BIM a serem seguidos', 'Métricas de qualidade', 'Processos de validação'],
      icon: CheckCircle
    },
    {
      area: 'Gestão dos Recursos',
      items: ['Equipe BIM necessária', 'Infraestrutura de hardware', 'Softwares e licenças'],
      icon: Users
    },
    {
      area: 'Gestão das Comunicações',
      items: ['Fluxos de informação BIM', 'Protocolos de troca de dados', 'Ambiente comum de dados (CDE)'],
      icon: Building2
    },
    {
      area: 'Gestão dos Riscos',
      items: ['Riscos de implementação', 'Resistência à mudança', 'Estratégias de mitigação'],
      icon: Lightbulb
    }
  ];

  const questionnaireAreas = [
    { area: 'Estratégia Organizacional', questions: 12 },
    { area: 'Processos Atuais', questions: 15 },
    { area: 'Tecnologia e Infraestrutura', questions: 10 },
    { area: 'Competências da Equipe', questions: 14 },
    { area: 'Projetos e Portfólio', questions: 8 },
    { area: 'Colaboração e Comunicação', questions: 11 },
    { area: 'Normas e Padronização', questions: 9 },
    { area: 'Expectativas de ROI', questions: 6 }
  ];

  return (
    <div className="min-h-screen bg-black">
      <SEO 
        title="Metodologia BIM - Implantação e Implementação"
        description="Consultoria especializada em implantação BIM. Diagnóstico, planejamento estratégico e implementação completa baseada no PMBOK. Transforme sua empresa com metodologia comprovada."
        keywords="metodologia BIM, implantação BIM, consultoria BIM, PMBOK, planejamento BIM, diagnóstico BIM"
      />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section - Componente Modular */}
        <MethodologyHero onBack={() => navigate('/')} />

        {/* Implantação vs Implementação - Componente Modular */}
        <ImplantacaoVsImplementacao />

        {/* ETAPA 1 - Levantamento de Informações - Componente Modular */}
        <EtapaLevantamento 
          pmbokAreas={pmbokAreas}
          questionnaireAreas={questionnaireAreas}
          scrollToMaturity={scrollToMaturity}
        />

        {/* BIM Maturity Assessment Section */}
        <div ref={maturityRef}>
          {showMaturityAssessment && <BIMMaturityAssessment />}
        </div>

        {/* ETAPA 2 - Diagnóstico */}
        <section className="py-20 bg-zinc-950">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {/* Header da Etapa */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">2</span>
                </div>
                <div>
                  <Badge className="bg-green-600/20 text-green-400 border-green-500/30 mb-2">
                    Duração: 3-6 semanas
                  </Badge>
                  <h2 className="text-3xl font-bold text-white">Diagnóstico</h2>
                </div>
              </div>

              <p className="text-lg text-gray-300 mb-10 max-w-3xl">
                Assim como um <strong className="text-white">médico analisa exames</strong> para determinar o tratamento ideal, 
                nós analisamos todos os dados coletados na etapa anterior para criar um diagnóstico completo e preciso. 
                Este documento será o <strong className="text-white">mapa estratégico</strong> que guiará toda a implementação BIM 
                na sua organização.
              </p>

              {/* Analogia Médica */}
              <div className="mb-12">
                <div className="bg-gradient-to-r from-blue-900/20 to-zinc-900 border border-blue-500/30 rounded-2xl p-8">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 bg-blue-600/20 rounded-full flex items-center justify-center">
                        <FileText className="text-blue-400" size={48} />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-4">Da Análise à Prescrição</h3>
                      <p className="text-gray-300 mb-4">
                        Quando você vai ao médico, ele não prescreve um tratamento sem antes analisar seus exames, 
                        entender seu histórico e avaliar suas condições. <strong className="text-white">O mesmo princípio se aplica ao BIM.</strong>
                      </p>
                      <p className="text-gray-400">
                        Com base nas +250 perguntas respondidas e nas 8 áreas analisadas, desenvolvemos um diagnóstico 
                        que identifica exatamente quais tratamentos sua organização precisa para uma implementação BIM 
                        saudável e sustentável.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* O que o Diagnóstico Define */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <Target className="text-red-500" />
                  O Que Será Definido no Diagnóstico
                </h3>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all">
                    <CardHeader>
                      <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-3">
                        <Workflow className="text-red-500" size={24} />
                      </div>
                      <CardTitle className="text-white text-lg">Tecnologias e Softwares</CardTitle>
                    </CardHeader>
                    <CardContent className="text-gray-400 text-sm">
                      <ul className="space-y-2">
                        <li>• Quais softwares BIM adotar</li>
                        <li>• Ferramentas de colaboração</li>
                        <li>• Ambiente Comum de Dados (CDE)</li>
                        <li>• Requisitos de hardware</li>
                        <li>• Integrações necessárias</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all">
                    <CardHeader>
                      <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center mb-3">
                        <ClipboardList className="text-blue-500" size={24} />
                      </div>
                      <CardTitle className="text-white text-lg">Processos de Trabalho</CardTitle>
                    </CardHeader>
                    <CardContent className="text-gray-400 text-sm">
                      <ul className="space-y-2">
                        <li>• Fluxos de trabalho BIM</li>
                        <li>• Procedimentos de modelagem</li>
                        <li>• Protocolos de coordenação</li>
                        <li>• Ciclos de revisão</li>
                        <li>• Gestão de mudanças</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all">
                    <CardHeader>
                      <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center mb-3">
                        <Users className="text-green-500" size={24} />
                      </div>
                      <CardTitle className="text-white text-lg">Capacitação de Equipe</CardTitle>
                    </CardHeader>
                    <CardContent className="text-gray-400 text-sm">
                      <ul className="space-y-2">
                        <li>• Trilhas de aprendizado</li>
                        <li>• Competências necessárias</li>
                        <li>• Cronograma de treinamentos</li>
                        <li>• Papéis e responsabilidades</li>
                        <li>• Mentoria e suporte</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all">
                    <CardHeader>
                      <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-3">
                        <FileText className="text-purple-500" size={24} />
                      </div>
                      <CardTitle className="text-white text-lg">Padrões e Templates</CardTitle>
                    </CardHeader>
                    <CardContent className="text-gray-400 text-sm">
                      <ul className="space-y-2">
                        <li>• Templates de projetos</li>
                        <li>• Bibliotecas de famílias</li>
                        <li>• Padrões de nomenclatura</li>
                        <li>• Tabelas de LOD/LOI</li>
                        <li>• Checklists de qualidade</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all">
                    <CardHeader>
                      <div className="w-12 h-12 bg-orange-600/20 rounded-lg flex items-center justify-center mb-3">
                        <Building2 className="text-orange-500" size={24} />
                      </div>
                      <CardTitle className="text-white text-lg">Infraestrutura</CardTitle>
                    </CardHeader>
                    <CardContent className="text-gray-400 text-sm">
                      <ul className="space-y-2">
                        <li>• Servidores e storage</li>
                        <li>• Rede e conectividade</li>
                        <li>• Backup e segurança</li>
                        <li>• Licenciamento</li>
                        <li>• Cloud vs On-premise</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all">
                    <CardHeader>
                      <div className="w-12 h-12 bg-cyan-600/20 rounded-lg flex items-center justify-center mb-3">
                        <TrendingUp className="text-cyan-500" size={24} />
                      </div>
                      <CardTitle className="text-white text-lg">Roadmap de Implementação</CardTitle>
                    </CardHeader>
                    <CardContent className="text-gray-400 text-sm">
                      <ul className="space-y-2">
                        <li>• Fases de implantação</li>
                        <li>• Marcos e entregas</li>
                        <li>• Projetos piloto</li>
                        <li>• Métricas de sucesso</li>
                        <li>• Cronograma detalhado</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Documento Final */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <Download className="text-red-500" />
                  Documento de Diagnóstico Completo
                </h3>
                
                <div className="bg-gradient-to-r from-red-900/20 to-zinc-900 border border-red-500/30 rounded-2xl p-8">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="text-6xl font-bold text-red-500">~300</div>
                        <div className="text-left">
                          <p className="text-2xl font-bold text-white">Páginas</p>
                          <p className="text-gray-400">de conteúdo estratégico</p>
                        </div>
                      </div>
                      <p className="text-gray-300 mb-6">
                        O documento de diagnóstico é um <strong className="text-white">material completo e personalizado</strong> 
                        para sua organização, contendo todas as análises, recomendações e planos de ação necessários 
                        para uma implementação BIM de sucesso.
                      </p>
                      <p className="text-gray-400 text-sm">
                        Este não é um documento genérico. Cada página é elaborada especificamente para a realidade 
                        da sua empresa, considerando seus processos, equipe, projetos e objetivos de negócio.
                      </p>
                    </div>
                    <div className="bg-zinc-900 rounded-xl p-6">
                      <h4 className="text-white font-bold mb-4">O documento inclui:</h4>
                      <ul className="space-y-3 text-gray-300 text-sm">
                        <li className="flex items-start gap-3">
                          <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={18} />
                          <span>Análise completa da situação atual (As-Is)</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={18} />
                          <span>Visão detalhada do estado futuro desejado (To-Be)</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={18} />
                          <span>Gap Analysis entre situação atual e objetivos</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={18} />
                          <span>Especificações técnicas de softwares e hardware</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={18} />
                          <span>Plano de capacitação com trilhas de aprendizado</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={18} />
                          <span>Cronograma de implementação fase a fase</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={18} />
                          <span>Estimativa de investimento e projeção de ROI</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={18} />
                          <span>Templates e padrões prontos para uso</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefícios do Diagnóstico */}
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-red-500 mb-2">100%</div>
                  <p className="text-gray-400 text-sm">Personalizado para sua empresa</p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-red-500 mb-2">Zero</div>
                  <p className="text-gray-400 text-sm">Achismos ou suposições</p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-red-500 mb-2">Claro</div>
                  <p className="text-gray-400 text-sm">Roadmap de implementação</p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-red-500 mb-2">Real</div>
                  <p className="text-gray-400 text-sm">ROI projetado com dados</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ETAPA 3 - Planejamento Estratégico */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {/* Header da Etapa */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">3</span>
                </div>
                <div>
                  <Badge className="bg-purple-600/20 text-purple-400 border-purple-500/30 mb-2">
                    Duração: 4-8 semanas
                  </Badge>
                  <h2 className="text-3xl font-bold text-white">Planejamento Estratégico</h2>
                </div>
              </div>

              <p className="text-lg text-gray-300 mb-10 max-w-3xl">
                Com o diagnóstico em mãos, é hora de criar o <strong className="text-white">plano de batalha</strong>. 
                O Planejamento Estratégico transforma todas as recomendações em um cronograma detalhado, 
                com análises financeiras, marcos de entrega e responsabilidades definidas.
              </p>

              {/* Gráfico de Gantt Estilizado */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <BarChart3 className="text-red-500" />
                  Cronograma de Implementação (Modelo Gantt)
                </h3>
                <p className="text-gray-400 mb-6">
                  Visualização clara de todas as fases, marcos e dependências do projeto de implementação BIM:
                </p>
                
                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 overflow-x-auto">
                  {/* Header do Gantt */}
                  <div className="min-w-[800px]">
                    <div className="grid grid-cols-13 gap-1 mb-4 text-center">
                      <div className="col-span-3 text-gray-400 text-sm font-medium text-left">Fase / Atividade</div>
                      {['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10'].map((month) => (
                        <div key={month} className="text-gray-500 text-xs">{month}</div>
                      ))}
                    </div>
                    
                    {/* Fase 1 - Preparação */}
                    <div className="mb-6">
                      <div className="grid grid-cols-13 gap-1 items-center mb-2">
                        <div className="col-span-3 text-white font-semibold text-sm flex items-center gap-2">
                          <div className="w-3 h-3 bg-blue-500 rounded"></div>
                          Fase 1: Preparação
                        </div>
                        <div className="col-span-2 h-6 bg-blue-500/30 rounded flex items-center justify-center">
                          <div className="h-3 w-full bg-blue-500 rounded mx-1"></div>
                        </div>
                        <div className="col-span-8"></div>
                      </div>
                      <div className="grid grid-cols-13 gap-1 items-center mb-1 pl-4">
                        <div className="col-span-3 text-gray-400 text-xs">• Aquisição de licenças</div>
                        <div className="col-span-1 h-4 bg-blue-400/20 rounded"><div className="h-2 w-full bg-blue-400 rounded m-1"></div></div>
                        <div className="col-span-9"></div>
                      </div>
                      <div className="grid grid-cols-13 gap-1 items-center mb-1 pl-4">
                        <div className="col-span-3 text-gray-400 text-xs">• Setup infraestrutura</div>
                        <div className="col-span-1"></div>
                        <div className="col-span-1 h-4 bg-blue-400/20 rounded"><div className="h-2 w-full bg-blue-400 rounded m-1"></div></div>
                        <div className="col-span-8"></div>
                      </div>
                      <div className="grid grid-cols-13 gap-1 items-center pl-4">
                        <div className="col-span-3 text-gray-400 text-xs">• Criação de templates</div>
                        <div className="col-span-1"></div>
                        <div className="col-span-2 h-4 bg-blue-400/20 rounded"><div className="h-2 w-full bg-blue-400 rounded m-1"></div></div>
                        <div className="col-span-7"></div>
                      </div>
                    </div>

                    {/* Fase 2 - Capacitação */}
                    <div className="mb-6">
                      <div className="grid grid-cols-13 gap-1 items-center mb-2">
                        <div className="col-span-3 text-white font-semibold text-sm flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-500 rounded"></div>
                          Fase 2: Capacitação
                        </div>
                        <div className="col-span-2"></div>
                        <div className="col-span-3 h-6 bg-green-500/30 rounded flex items-center justify-center">
                          <div className="h-3 w-full bg-green-500 rounded mx-1"></div>
                        </div>
                        <div className="col-span-5"></div>
                      </div>
                      <div className="grid grid-cols-13 gap-1 items-center mb-1 pl-4">
                        <div className="col-span-3 text-gray-400 text-xs">• Treinamento básico</div>
                        <div className="col-span-2"></div>
                        <div className="col-span-2 h-4 bg-green-400/20 rounded"><div className="h-2 w-full bg-green-400 rounded m-1"></div></div>
                        <div className="col-span-6"></div>
                      </div>
                      <div className="grid grid-cols-13 gap-1 items-center mb-1 pl-4">
                        <div className="col-span-3 text-gray-400 text-xs">• Treinamento avançado</div>
                        <div className="col-span-4"></div>
                        <div className="col-span-2 h-4 bg-green-400/20 rounded"><div className="h-2 w-full bg-green-400 rounded m-1"></div></div>
                        <div className="col-span-4"></div>
                      </div>
                      <div className="grid grid-cols-13 gap-1 items-center pl-4">
                        <div className="col-span-3 text-gray-400 text-xs">• Workshops práticos</div>
                        <div className="col-span-3"></div>
                        <div className="col-span-3 h-4 bg-green-400/20 rounded"><div className="h-2 w-full bg-green-400 rounded m-1"></div></div>
                        <div className="col-span-4"></div>
                      </div>
                    </div>

                    {/* Fase 3 - Projeto Piloto */}
                    <div className="mb-6">
                      <div className="grid grid-cols-13 gap-1 items-center mb-2">
                        <div className="col-span-3 text-white font-semibold text-sm flex items-center gap-2">
                          <div className="w-3 h-3 bg-orange-500 rounded"></div>
                          Fase 3: Projeto Piloto
                        </div>
                        <div className="col-span-4"></div>
                        <div className="col-span-3 h-6 bg-orange-500/30 rounded flex items-center justify-center">
                          <div className="h-3 w-full bg-orange-500 rounded mx-1"></div>
                        </div>
                        <div className="col-span-3"></div>
                      </div>
                      <div className="grid grid-cols-13 gap-1 items-center mb-1 pl-4">
                        <div className="col-span-3 text-gray-400 text-xs">• Seleção do projeto</div>
                        <div className="col-span-4"></div>
                        <div className="col-span-1 h-4 bg-orange-400/20 rounded"><div className="h-2 w-full bg-orange-400 rounded m-1"></div></div>
                        <div className="col-span-5"></div>
                      </div>
                      <div className="grid grid-cols-13 gap-1 items-center mb-1 pl-4">
                        <div className="col-span-3 text-gray-400 text-xs">• Execução piloto</div>
                        <div className="col-span-5"></div>
                        <div className="col-span-2 h-4 bg-orange-400/20 rounded"><div className="h-2 w-full bg-orange-400 rounded m-1"></div></div>
                        <div className="col-span-3"></div>
                      </div>
                      <div className="grid grid-cols-13 gap-1 items-center pl-4">
                        <div className="col-span-3 text-gray-400 text-xs">• Lições aprendidas</div>
                        <div className="col-span-6"></div>
                        <div className="col-span-1 h-4 bg-orange-400/20 rounded"><div className="h-2 w-full bg-orange-400 rounded m-1"></div></div>
                        <div className="col-span-3"></div>
                      </div>
                    </div>

                    {/* Marcos */}
                    <div className="border-t border-zinc-700 pt-4 mt-6">
                      <div className="grid grid-cols-13 gap-1 items-center">
                        <div className="col-span-3 text-gray-500 text-xs">Marcos Principais</div>
                        <div className="col-span-2 flex justify-center">
                          <div className="w-4 h-4 bg-yellow-500 rotate-45 flex items-center justify-center" title="Kickoff">
                          </div>
                        </div>
                        <div className="col-span-3"></div>
                        <div className="col-span-2 flex justify-center">
                          <div className="w-4 h-4 bg-yellow-500 rotate-45" title="Fim Implantação"></div>
                        </div>
                        <div className="col-span-3"></div>
                      </div>
                      <div className="grid grid-cols-13 gap-1 mt-1">
                        <div className="col-span-3"></div>
                        <div className="col-span-2 text-center text-yellow-500 text-xs">Kickoff</div>
                        <div className="col-span-3"></div>
                        <div className="col-span-2 text-center text-yellow-500 text-xs">Entrega</div>
                        <div className="col-span-3"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Análises Financeiras */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <TrendingUp className="text-red-500" />
                  Análises Financeiras
                </h3>
                <p className="text-gray-400 mb-6">
                  Projeções detalhadas de investimento, retorno e payback para tomada de decisão fundamentada:
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  {/* Investimento */}
                  <Card className="bg-gradient-to-br from-blue-900/30 to-zinc-900 border-blue-500/30">
                    <CardHeader>
                      <CardTitle className="text-blue-400 text-lg flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                          <span className="text-blue-400 text-lg">$</span>
                        </div>
                        Investimento Total
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">Software & Licenças</span>
                          <div className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{width: '35%'}}></div>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">Hardware</span>
                          <div className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{width: '25%'}}></div>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">Capacitação</span>
                          <div className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{width: '20%'}}></div>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">Consultoria</span>
                          <div className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{width: '20%'}}></div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* ROI Projetado */}
                  <Card className="bg-gradient-to-br from-green-900/30 to-zinc-900 border-green-500/30">
                    <CardHeader>
                      <CardTitle className="text-green-400 text-lg flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                          <TrendingUp className="text-green-400" size={18} />
                        </div>
                        ROI Projetado
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center mb-4">
                        <div className="text-4xl font-bold text-green-400">150-300%</div>
                        <p className="text-gray-500 text-sm">em 24-36 meses</p>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-gray-400">
                          <span>Redução de retrabalho</span>
                          <span className="text-green-400">-40%</span>
                        </div>
                        <div className="flex justify-between text-gray-400">
                          <span>Ganho produtividade</span>
                          <span className="text-green-400">+30%</span>
                        </div>
                        <div className="flex justify-between text-gray-400">
                          <span>Redução de RFIs</span>
                          <span className="text-green-400">-50%</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Payback */}
                  <Card className="bg-gradient-to-br from-purple-900/30 to-zinc-900 border-purple-500/30">
                    <CardHeader>
                      <CardTitle className="text-purple-400 text-lg flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                          <ArrowRight className="text-purple-400" size={18} />
                        </div>
                        Payback Estimado
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="relative pt-4">
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-500 text-xs">Investimento</span>
                          <span className="text-gray-500 text-xs">Payback</span>
                          <span className="text-gray-500 text-xs">Lucro</span>
                        </div>
                        <div className="h-3 bg-zinc-700 rounded-full overflow-hidden flex">
                          <div className="h-full bg-red-500" style={{width: '30%'}}></div>
                          <div className="h-full bg-yellow-500" style={{width: '25%'}}></div>
                          <div className="h-full bg-green-500" style={{width: '45%'}}></div>
                        </div>
                        <div className="flex justify-between mt-2">
                          <span className="text-red-400 text-xs">0-12m</span>
                          <span className="text-yellow-400 text-xs">12-18m</span>
                          <span className="text-green-400 text-xs">18m+</span>
                        </div>
                      </div>
                      <p className="text-center text-gray-400 text-sm mt-4">
                        Payback típico: <strong className="text-white">12-18 meses</strong>
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Entregáveis do Planejamento */}
              <div className="bg-gradient-to-r from-purple-900/20 to-zinc-900 border border-purple-500/30 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 text-center">
                  Documentos Entregues no Planejamento
                </h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <FileText className="text-purple-400 mx-auto mb-2" size={32} />
                    <p className="text-white font-medium text-sm">BEP</p>
                    <p className="text-gray-500 text-xs">BIM Execution Plan</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <ClipboardList className="text-purple-400 mx-auto mb-2" size={32} />
                    <p className="text-white font-medium text-sm">Cronograma</p>
                    <p className="text-gray-500 text-xs">Gantt Detalhado</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <TrendingUp className="text-purple-400 mx-auto mb-2" size={32} />
                    <p className="text-white font-medium text-sm">Business Case</p>
                    <p className="text-gray-500 text-xs">Análise Financeira</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <Target className="text-purple-400 mx-auto mb-2" size={32} />
                    <p className="text-white font-medium text-sm">KPIs</p>
                    <p className="text-gray-500 text-xs">Métricas de Sucesso</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA - Teste seu Conhecimento em Revit */}
        <section className="py-16 bg-gradient-to-r from-red-900/30 via-black to-red-900/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-500/30 mb-4">
                Avaliação Gratuita
              </Badge>
              <h2 className="text-3xl font-bold text-white mb-4">
                🎯 Teste seu Conhecimento em <span className="text-red-500">Revit</span>
              </h2>
              <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
                Antes de investir em capacitação, descubra seu nível atual em Autodesk Revit. 
                Avalie seus conhecimentos em Arquitetura Avançado, Bibliotecas e Parametrização. 
                Ao final, receba um certificado ou recomendações de cursos personalizadas.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  onClick={() => navigate('/avaliacao-revit')}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-6 text-lg"
                >
                  TESTE SEU CONHECIMENTO EM REVIT
                </Button>
              </div>
              <div className="flex justify-center gap-8 mt-8 text-sm text-gray-500">
                <span>✓ 20 questões</span>
                <span>✓ Resultado imediato</span>
                <span>✓ Certificado ao passar</span>
              </div>
            </div>
          </div>
        </section>

        {/* Divisor entre Implantação e Implementação */}
        <section className="py-16 bg-gradient-to-b from-zinc-900 via-black to-zinc-950">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="h-px w-20 bg-gradient-to-r from-transparent to-blue-500"></div>
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <div className="h-px w-16 bg-gradient-to-r from-blue-500 to-red-500"></div>
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="h-px w-20 bg-gradient-to-l from-transparent to-red-500"></div>
              </div>
              <p className="text-gray-500 text-sm uppercase tracking-widest mb-2">Próxima Fase</p>
              <h2 className="text-2xl font-bold text-white">
                Com a <span className="text-blue-400">Implantação</span> concluída, é hora da <span className="text-red-400">Implementação</span>
              </h2>
            </div>
          </div>
        </section>

        {/* Implementação BIM */}
        <section className="py-20 bg-zinc-950">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {/* Header da Seção */}
              <div className="text-center mb-16">
                <div className="inline-block bg-red-600/10 border border-red-500/30 rounded-2xl px-8 py-6 mb-6">
                  <Badge className="bg-red-600/30 text-red-400 border-red-500/50 mb-3 text-sm">
                    Fase 2 do Processo
                  </Badge>
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">
                    Etapas da <span className="text-red-500">Implementação</span>
                  </h2>
                  <p className="text-red-400/80 text-sm">Duração estimada: 12 a 18 meses</p>
                </div>
                <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                  Esta é a etapa onde tudo se torna realidade. Aqui acontecem os <strong className="text-white">investimentos em tecnologia</strong>, 
                  a <strong className="text-white">capacitação das equipes</strong>, o <strong className="text-white">desenvolvimento dos documentos técnicos</strong> 
                  e o <strong className="text-white">projeto piloto</strong>. O objetivo final é que sua empresa seja 
                  <strong className="text-red-400"> 100% autossuficiente</strong> em BIM.
                </p>
              </div>

              {/* Investimentos */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <Building2 className="text-red-500" />
                  Investimentos em Tecnologia e Capacitação
                </h3>
                <p className="text-gray-400 mb-6">
                  Momento de colocar em prática o planejamento, adquirindo as ferramentas e desenvolvendo as competências necessárias:
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Softwares e Equipamentos */}
                  <Card className="bg-gradient-to-br from-blue-900/30 to-zinc-900 border-blue-500/30">
                    <CardHeader>
                      <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-3">
                        <Workflow className="text-blue-400" size={28} />
                      </div>
                      <CardTitle className="text-blue-400 text-xl">Softwares e Equipamentos</CardTitle>
                      <CardDescription className="text-gray-400">
                        Aquisição das ferramentas definidas no diagnóstico
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={18} />
                          <span>Licenças de softwares BIM (Revit, Navisworks, etc.)</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={18} />
                          <span>Ambiente Comum de Dados (CDE)</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={18} />
                          <span>Workstations e hardware adequado</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={18} />
                          <span>Servidores e infraestrutura de rede</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={18} />
                          <span>Plugins e ferramentas complementares</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Capacitação */}
                  <Card className="bg-gradient-to-br from-green-900/30 to-zinc-900 border-green-500/30">
                    <CardHeader>
                      <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-3">
                        <Users className="text-green-400" size={28} />
                      </div>
                      <CardTitle className="text-green-400 text-xl">Capacitação das Equipes</CardTitle>
                      <CardDescription className="text-gray-400">
                        Desenvolvimento das competências técnicas e processuais
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="text-green-400 mt-0.5 flex-shrink-0" size={18} />
                          <span>Treinamentos em softwares BIM</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="text-green-400 mt-0.5 flex-shrink-0" size={18} />
                          <span>Capacitação em processos e workflows</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="text-green-400 mt-0.5 flex-shrink-0" size={18} />
                          <span>Formação de BIM Managers internos</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="text-green-400 mt-0.5 flex-shrink-0" size={18} />
                          <span>Workshops de coordenação e clash detection</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="text-green-400 mt-0.5 flex-shrink-0" size={18} />
                          <span>Certificações e especializações</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Documentos Técnicos */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <FileText className="text-red-500" />
                  Documentos Técnicos de Apoio
                </h3>
                <p className="text-gray-400 mb-6">
                  Desenvolvimento e entrega de toda a documentação técnica necessária para padronização e governança BIM:
                </p>
                
                <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center hover:border-red-500/50 transition-all">
                    <div className="w-14 h-14 bg-red-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <FileText className="text-red-500" size={28} />
                    </div>
                    <h4 className="text-white font-bold mb-2">BIM Mandate</h4>
                    <p className="text-gray-500 text-xs">
                      Diretriz organizacional que estabelece a obrigatoriedade e direcionamento do uso de BIM
                    </p>
                  </div>

                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center hover:border-red-500/50 transition-all">
                    <div className="w-14 h-14 bg-blue-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <ClipboardList className="text-blue-500" size={28} />
                    </div>
                    <h4 className="text-white font-bold mb-2">Plano de Execução BIM</h4>
                    <p className="text-gray-500 text-xs">
                      BEP detalhado com processos, responsabilidades e entregas para cada fase do projeto
                    </p>
                  </div>

                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center hover:border-red-500/50 transition-all">
                    <div className="w-14 h-14 bg-purple-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Workflow className="text-purple-500" size={28} />
                    </div>
                    <h4 className="text-white font-bold mb-2">Templates</h4>
                    <p className="text-gray-500 text-xs">
                      Templates de projeto padronizados com views, sheets, parâmetros e configurações
                    </p>
                  </div>

                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center hover:border-red-500/50 transition-all">
                    <div className="w-14 h-14 bg-orange-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Building2 className="text-orange-500" size={28} />
                    </div>
                    <h4 className="text-white font-bold mb-2">Bibliotecas</h4>
                    <p className="text-gray-500 text-xs">
                      Famílias e componentes BIM customizados para a realidade da sua empresa
                    </p>
                  </div>

                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center hover:border-red-500/50 transition-all">
                    <div className="w-14 h-14 bg-green-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="text-green-500" size={28} />
                    </div>
                    <h4 className="text-white font-bold mb-2">Checklists</h4>
                    <p className="text-gray-500 text-xs">
                      Listas de verificação para controle de qualidade em cada etapa do projeto
                    </p>
                  </div>

                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center hover:border-red-500/50 transition-all">
                    <div className="w-14 h-14 bg-cyan-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <FileText className="text-cyan-500" size={28} />
                    </div>
                    <h4 className="text-white font-bold mb-2">Contratos BIM</h4>
                    <p className="text-gray-500 text-xs">
                      Apoio na criação de contratos e adequação à legislação BIM vigente
                    </p>
                  </div>
                </div>
              </div>

              {/* Projeto Piloto */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <Target className="text-red-500" />
                  Projeto Piloto: 2 Momentos Estratégicos
                </h3>
                <p className="text-gray-400 mb-6">
                  O projeto piloto é dividido em dois momentos complementares, garantindo domínio completo da tecnologia e dos processos:
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Momento 1 */}
                  <div className="bg-gradient-to-br from-purple-900/20 to-zinc-900 border border-purple-500/30 rounded-2xl p-6">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-purple-600 rounded-xl flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">1º</span>
                      </div>
                      <div>
                        <Badge className="bg-purple-600/20 text-purple-400 border-purple-500/30 mb-1">
                          Fase de Fortalecimento
                        </Badge>
                        <h4 className="text-white font-bold text-lg">Domínio das Tecnologias</h4>
                      </div>
                    </div>
                    
                    <p className="text-gray-300 mb-4">
                      Foco total em <strong className="text-white">eliminar todas as dúvidas</strong> sobre os softwares 
                      e ferramentas adquiridos. É o momento de explorar, experimentar e dominar a tecnologia.
                    </p>
                    
                    <div className="bg-zinc-900/50 rounded-xl p-4 mb-4">
                      <h5 className="text-purple-400 font-medium mb-3">O que acontece neste momento:</h5>
                      <ul className="space-y-2 text-sm text-gray-400">
                        <li className="flex items-start gap-2">
                          <Lightbulb className="text-purple-400 mt-0.5 flex-shrink-0" size={16} />
                          <span>Sessões intensivas de tira-dúvidas sobre softwares</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Lightbulb className="text-purple-400 mt-0.5 flex-shrink-0" size={16} />
                          <span>Exercícios práticos com cenários reais da empresa</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Lightbulb className="text-purple-400 mt-0.5 flex-shrink-0" size={16} />
                          <span>Configuração personalizada das ferramentas</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Lightbulb className="text-purple-400 mt-0.5 flex-shrink-0" size={16} />
                          <span>Documentação de melhores práticas internas</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Lightbulb className="text-purple-400 mt-0.5 flex-shrink-0" size={16} />
                          <span>Validação dos templates e bibliotecas criados</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="flex items-center gap-2 text-purple-400 text-sm">
                      <CheckCircle size={16} />
                      <span>Resultado: Equipe 100% confiante nas ferramentas</span>
                    </div>
                  </div>

                  {/* Momento 2 */}
                  <div className="bg-gradient-to-br from-orange-900/20 to-zinc-900 border border-orange-500/30 rounded-2xl p-6">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-orange-600 rounded-xl flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">2º</span>
                      </div>
                      <div>
                        <Badge className="bg-orange-600/20 text-orange-400 border-orange-500/30 mb-1">
                          Fase de Aplicação Real
                        </Badge>
                        <h4 className="text-white font-bold text-lg">Projeto Real Acompanhado</h4>
                      </div>
                    </div>
                    
                    <p className="text-gray-300 mb-4">
                      Acompanhamento de um <strong className="text-white">projeto real da empresa</strong>, 
                      lado a lado com o BIM Manager do cliente, <strong className="text-white">aparando todas as arestas</strong> e 
                      garantindo a aplicação correta dos processos.
                    </p>
                    
                    <div className="bg-zinc-900/50 rounded-xl p-4 mb-4">
                      <h5 className="text-orange-400 font-medium mb-3">O que acontece neste momento:</h5>
                      <ul className="space-y-2 text-sm text-gray-400">
                        <li className="flex items-start gap-2">
                          <Users className="text-orange-400 mt-0.5 flex-shrink-0" size={16} />
                          <span>Consultoria lado a lado com BIM Manager interno</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Users className="text-orange-400 mt-0.5 flex-shrink-0" size={16} />
                          <span>Aplicação dos processos em projeto real</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Users className="text-orange-400 mt-0.5 flex-shrink-0" size={16} />
                          <span>Resolução de problemas em tempo real</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Users className="text-orange-400 mt-0.5 flex-shrink-0" size={16} />
                          <span>Ajustes finos em processos e workflows</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Users className="text-orange-400 mt-0.5 flex-shrink-0" size={16} />
                          <span>Transferência completa de conhecimento</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="flex items-center gap-2 text-orange-400 text-sm">
                      <CheckCircle size={16} />
                      <span>Resultado: Equipe pronta para voar solo</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Objetivo Final - Autossuficiência */}
              <div className="bg-gradient-to-r from-red-900/30 to-zinc-900 border border-red-500/30 rounded-2xl p-8">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="text-white" size={40} />
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-4">
                    Objetivo Final: <span className="text-red-500">Autossuficiência Total</span>
                  </h3>
                  <p className="text-gray-300 max-w-2xl mx-auto">
                    Ao final da implementação, sua empresa estará <strong className="text-white">completamente independente</strong>. 
                    Você não precisará mais da consultoria para operar, evoluir ou resolver desafios BIM. 
                    <strong className="text-red-400"> Esse é o nosso compromisso.</strong>
                  </p>
                </div>

                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-green-400 mb-2">100%</div>
                    <p className="text-gray-400 text-sm">Equipe Capacitada</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-blue-400 mb-2">100%</div>
                    <p className="text-gray-400 text-sm">Processos Documentados</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-purple-400 mb-2">100%</div>
                    <p className="text-gray-400 text-sm">Ferramentas Dominadas</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-red-400 mb-2">0%</div>
                    <p className="text-gray-400 text-sm">Dependência Externa</p>
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <p className="text-gray-400 text-sm italic">
                    &ldquo;Nosso sucesso é medido pela sua independência. Quando você não precisar mais de nós, 
                    teremos cumprido nossa missão.&rdquo;
                  </p>
                  <p className="text-red-400 text-sm mt-2 font-medium">— Metodologia Justi</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Botão Voltar ao Início */}
        <section className="py-12 bg-black">
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-800"
            >
              <ArrowLeft className="mr-2" size={18} />
              Voltar ao Início
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MethodologyPage;
